// @unocss-include
import { jsx as _jsx } from "react/jsx-runtime";
import { debounce } from '@a-type/utils';
import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState, } from 'react';
class MasonryLayout {
    constructor(config) {
        this.config = config;
        this.container = null;
        this.columns = 0;
        this.updateConfig = (config) => {
            var _a, _b;
            const gapChanged = config.gap !== this.config.gap;
            this.config = config;
            // hacky way to avoid updating twice...
            if (!this.updateFromContainerSize((_b = (_a = this.container) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0) &&
                gapChanged) {
                this.relayout();
            }
        };
        this.updateFromContainerSize = (containerWidth) => {
            if (typeof this.config.columns === 'function') {
                const newValue = this.config.columns(containerWidth);
                if (newValue !== this.columns) {
                    this.columns = newValue;
                    this.relayout();
                    return true;
                }
            }
            else if (this.config.columns !== this.columns) {
                this.columns = this.config.columns;
                this.relayout();
                return true;
            }
            return false;
        };
        this.relayout = debounce(() => {
            if (!this.container) {
                return;
            }
            const tracks = new Array(this.columns).fill(0);
            const gap = this.config.gap;
            // percentage-based width and x position so that items automatically
            // layout correctly when the container is resized (as long as columns
            // are the same)
            const pixelColumnWidthMinusGap = (this.container.offsetWidth - gap * (this.columns - 1)) / this.columns;
            const columnPercentageWidth = (pixelColumnWidthMinusGap / this.container.offsetWidth) * 100;
            const gapPercentageWidth = (gap / this.container.offsetWidth) * 100;
            const children = Array.from(this.container.children);
            children.forEach((child, index) => {
                let itemTrackSpan = parseInt(child.getAttribute('data-span') || '1');
                if (isNaN(itemTrackSpan) || itemTrackSpan < 1) {
                    itemTrackSpan = 1;
                }
                if (itemTrackSpan > this.columns) {
                    itemTrackSpan = this.columns;
                }
                const trackIndex = pickTrack(tracks, itemTrackSpan);
                const affectedTracks = tracks.slice(trackIndex, trackIndex + itemTrackSpan);
                const x = (columnPercentageWidth + gapPercentageWidth) * trackIndex;
                const y = Math.max(...affectedTracks);
                const width = columnPercentageWidth * itemTrackSpan +
                    gapPercentageWidth * (itemTrackSpan - 1);
                child.style.setProperty('position', 'absolute');
                child.style.setProperty('visibility', 'visible');
                child.style.setProperty('width', `${width}%`);
                child.style.setProperty('left', `${x}%`);
                child.style.setProperty('top', `${y}px`);
                child.style.setProperty('z-index', index.toString());
                requestAnimationFrame(() => {
                    child.classList.add('transition-all');
                });
                for (let i = 0; i < itemTrackSpan; i++) {
                    tracks[trackIndex + i] = y + child.offsetHeight + gap;
                }
            });
            this.container.style.setProperty('height', `${Math.max(...tracks)}px`);
        }, 100);
        this.columns =
            typeof config.columns === 'function' ? config.columns(0) : config.columns;
        this.relayout();
    }
}
class ClientLayout extends MasonryLayout {
    constructor(config) {
        super(config);
        this.containerResizeObserver = null;
        this.containerMutationObserver = null;
        this.handleContainerMutation = (entries) => {
            for (const entry of entries) {
                entry.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement) {
                        this.setupChild(node);
                    }
                });
                entry.removedNodes.forEach((node) => {
                    var _a;
                    if (node instanceof HTMLElement) {
                        (_a = this.childSizeObserver) === null || _a === void 0 ? void 0 : _a.unobserve(node);
                    }
                });
            }
            this.relayout();
        };
        this.handleChildResize = (entries) => {
            // only worry about height changes
            for (const entry of entries) {
                const lastSeenHeight = entry.target.getAttribute('data-last-height');
                const currentHeight = entry.contentRect.height;
                entry.target.setAttribute('data-last-height', currentHeight.toString());
                if (lastSeenHeight && lastSeenHeight !== currentHeight.toString()) {
                    this.relayout();
                }
            }
        };
        this.attach = (container) => {
            var _a, _b;
            const disconnect = () => {
                var _a, _b;
                (_a = this.containerResizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
                (_b = this.containerMutationObserver) === null || _b === void 0 ? void 0 : _b.disconnect();
                container.style.removeProperty('position');
                container.style.removeProperty('overflow');
                this.container = null;
            };
            if (container === this.container) {
                return disconnect;
            }
            (_a = this.containerResizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
            (_b = this.containerMutationObserver) === null || _b === void 0 ? void 0 : _b.disconnect();
            this.columns = 0;
            this.container = container;
            this.containerResizeObserver = new ResizeObserver(this.handleContainerResize);
            this.containerMutationObserver = new MutationObserver(this.handleContainerMutation);
            this.containerResizeObserver.observe(container);
            this.containerMutationObserver.observe(container, { childList: true });
            container.style.setProperty('position', 'relative');
            container.style.setProperty('overflow', 'hidden');
            container.style.setProperty('visibility', 'visible');
            container.childNodes.forEach((node) => {
                if (node instanceof HTMLElement) {
                    this.setupChild(node);
                }
            });
            if (!this.updateFromContainerSize(container.offsetWidth)) {
                this.relayout();
            }
            return disconnect;
        };
        this.setupChild = (child) => {
            child.style.setProperty('position', 'absolute');
            // hide until laid out
            child.style.setProperty('visibility', 'hidden');
            this.childSizeObserver.observe(child);
            this.childMutationObserver.observe(child, {
                attributeFilter: ['data-span'],
            });
        };
        this.handleContainerResize = (entries) => {
            const containerWidth = entries[0].contentRect.width;
            this.updateFromContainerSize(containerWidth);
        };
        this.childSizeObserver = new ResizeObserver(this.handleChildResize);
        this.childMutationObserver = new MutationObserver(this.relayout);
    }
}
class ServerLayout extends MasonryLayout {
    constructor() {
        super(...arguments);
        this.attach = (container) => {
            this.container = container;
            this.container.style.setProperty('position', 'relative');
            this.container.style.setProperty('overflow', 'hidden');
            this.container.style.setProperty('visibility', 'visible');
            container.childNodes.forEach((node) => {
                if (node instanceof HTMLElement) {
                    this.setupChild(node);
                }
            });
            this.updateFromContainerSize(container.offsetWidth);
            return () => { };
        };
    }
    setupChild(child) {
        child.style.setProperty('position', 'absolute');
        child.style.setProperty('visibility', 'visible');
    }
}
function pickTrack(tracks, trackSpan) {
    const subTracks = tracks.slice(0, tracks.length - trackSpan + 1);
    const min = Math.min(...subTracks);
    const index = subTracks.indexOf(min);
    if (index === -1) {
        return 0;
    }
    return index;
}
const initialStyle = {
    height: 0,
    position: 'relative',
    overflow: 'hidden',
    visibility: 'hidden',
};
export function Masonry({ className, children, columns = 3, gap = 16, }) {
    const [layout] = useState(() => {
        if (typeof window === 'undefined') {
            return new ServerLayout({ columns, gap });
        }
        return new ClientLayout({ columns, gap });
    });
    useIsomorphicLayoutEffect(() => {
        layout.updateConfig({ columns, gap });
    }, [layout, columns, gap]);
    const ref = useRef(null);
    useIsomorphicLayoutEffect(() => {
        if (ref.current) {
            return layout.attach(ref.current);
        }
    }, [layout, ref]);
    return (_jsx("div", { ref: ref, style: initialStyle, className: clsx('layer-components:z-0', className), children: children }));
}
export function masonrySpan(span) {
    return { 'data-span': span };
}
function useIsomorphicLayoutEffect(effect, deps = []) {
    const isBrowser = typeof window !== 'undefined';
    const useIsoEffect = isBrowser ? useLayoutEffect : useEffect;
    return useIsoEffect(effect, deps);
}
//# sourceMappingURL=masonry.js.map